/*
 * Collection filters and sort (design-system.md 4.13).
 *
 * Custom elements
 *   <facet-filters>  wraps the toolbar, the filter form, the chips and the
 *                    grid. Filters and sorts the product cards in place using
 *                    the data-* attributes the product-card snippet writes:
 *                    data-price (cents), data-available, data-created-at and
 *                    data-collections (space-separated handles).
 *   <facet-drawer>   the filter drawer below 990px (extends ModalDrawer).
 *                    From 990px the same markup is a static sidebar, so the
 *                    element drops its hidden attribute and dialog role.
 *
 * Filters
 *   Subcategory   checkboxes, OR within the group
 *   Price         whole-dollar min and max, inclusive
 *   Availability  "In stock only"
 *   Sort          manual (featured, the collection's own order),
 *                 price-ascending, price-descending, created-descending
 *
 * State is mirrored in the query string with Shopify's names
 * (filter.v.price.gte, filter.v.price.lte, filter.v.availability, sort_by)
 * plus filter.p.subcategory, so a filtered view can be reloaded or shared.
 * URL updates are wrapped in try/catch because sandboxed frames may refuse
 * history changes.
 */

import { ModalDrawer } from './global.js';

const desktopQuery = window.matchMedia('(min-width: 990px)');
const SORTS = new Set(['manual', 'price-ascending', 'price-descending', 'created-descending']);

const defineElement = (name, constructor) => {
  if (!customElements.get(name)) customElements.define(name, constructor);
};

const wholeDollars = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

/** Parses a whole-dollar input value. Empty or invalid input means "no bound". */
function parseDollars(value) {
  const text = String(value ?? '').trim();
  if (text === '') return null;
  const n = Math.floor(Number(text));
  return Number.isFinite(n) && n >= 0 ? n : null;
}

function debounce(fn, wait) {
  let timer = null;
  const debounced = (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), wait);
  };
  debounced.cancel = () => clearTimeout(timer);
  return debounced;
}

/* ------------------------------------------------------------------------ */
/* <facet-drawer>                                                            */
/* ------------------------------------------------------------------------ */

class FacetDrawer extends ModalDrawer {
  connectedCallback() {
    super.connectedCallback();
    if (this.facetReady) return;
    this.facetReady = true;
    this.sync = () => this.syncLayout();
    desktopQuery.addEventListener('change', this.sync);
    this.syncLayout();
  }

  disconnectedCallback() {
    desktopQuery.removeEventListener('change', this.sync);
  }

  syncLayout() {
    const panel = this.querySelector('.drawer__panel');
    if (desktopQuery.matches) {
      if (this.isOpen) this.close({ restoreFocus: false });
      // Skip the closing transition: the sidebar must be visible at once.
      if (this.cancelClose) this.cancelClose();
      this.cancelClose = null;
      this.classList.remove('is-closing');
      this.hidden = false;
      panel?.removeAttribute('role');
      panel?.removeAttribute('aria-modal');
      panel?.removeAttribute('tabindex');
    } else {
      panel?.setAttribute('role', 'dialog');
      panel?.setAttribute('aria-modal', 'true');
      panel?.setAttribute('tabindex', '-1');
      if (!this.isOpen) this.hidden = true;
    }
  }

  open(trigger) {
    if (desktopQuery.matches) return;
    super.open(trigger);
  }
}

/* ------------------------------------------------------------------------ */
/* <facet-filters>                                                           */
/* ------------------------------------------------------------------------ */

class FacetFilters extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;

    this.grid = this.querySelector('[data-facet-grid]');
    this.form = this.querySelector('[data-facet-form]');
    this.sortSelect = this.querySelector('[data-facet-sort]');
    this.subInputs = Array.from(this.querySelectorAll('[data-facet-subcategory]'));
    this.stockInput = this.querySelector('[data-facet-in-stock]');
    this.minInput = this.querySelector('[data-facet-price-min]');
    this.maxInput = this.querySelector('[data-facet-price-max]');
    this.countEl = this.querySelector('[data-facet-count]');
    this.statusEl = this.querySelector('[data-facet-status]');
    this.showButton = this.querySelector('[data-facet-show]');
    this.badge = this.querySelector('[data-facet-active-badge]');
    this.badgeLabel = this.querySelector('[data-facet-active-label]');
    this.chips = this.querySelector('[data-facet-chips]');
    this.chipList = this.querySelector('[data-facet-chip-list]');
    this.emptyState = this.querySelector('[data-facet-empty]');
    if (!this.grid) return;

    this.items = Array.from(this.grid.querySelectorAll(':scope > [data-facet-item]')).map((li, index) => {
      const card = li.querySelector('[data-product-card]');
      const data = card ? card.dataset : {};
      return {
        li,
        index,
        price: Number(data.price) || 0,
        available: data.available !== 'false',
        created: data.createdAt || '',
        collections: new Set(String(data.collections || '').split(/\s+/).filter(Boolean)),
      };
    });

    this.readUrl();
    this.writeInputs();
    this.apply({ announce: false, updateUrl: false });

    this.form?.addEventListener('submit', (event) => event.preventDefault());
    this.form?.addEventListener('change', (event) => {
      if (event.target === this.minInput || event.target === this.maxInput) {
        this.debouncedPrice.cancel();
      }
      this.readInputs();
      this.apply();
    });
    this.debouncedPrice = debounce(() => {
      this.readInputs();
      this.apply();
    }, 350);
    [this.minInput, this.maxInput].forEach((input) => input?.addEventListener('input', this.debouncedPrice));
    this.sortSelect?.addEventListener('change', () => {
      this.state.sort = SORTS.has(this.sortSelect.value) ? this.sortSelect.value : 'manual';
      this.apply({ announce: false });
    });

    this.addEventListener('click', (event) => {
      const clear = event.target.closest('[data-facet-clear]');
      if (clear) {
        event.preventDefault();
        this.clearAll(clear);
        return;
      }
      const chip = event.target.closest('[data-facet-chip]');
      if (chip) {
        event.preventDefault();
        this.removeChip(chip);
      }
    });
  }

  /* State ------------------------------------------------------------------ */

  get validSubs() {
    return new Set(this.subInputs.map((input) => input.value));
  }

  readUrl() {
    const state = { subs: new Set(), min: null, max: null, inStock: false, sort: 'manual' };
    let params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch {
      params = new URLSearchParams();
    }
    const valid = this.validSubs;
    params.getAll('filter.p.subcategory').forEach((handle) => {
      if (valid.has(handle)) state.subs.add(handle);
    });
    if (this.minInput) state.min = parseDollars(params.get('filter.v.price.gte'));
    if (this.maxInput) state.max = parseDollars(params.get('filter.v.price.lte'));
    state.inStock = Boolean(this.stockInput) && params.get('filter.v.availability') === '1';
    const sort = params.get('sort_by');
    if (sort && SORTS.has(sort)) state.sort = sort;
    this.state = state;
  }

  readInputs() {
    this.state.subs = new Set(this.subInputs.filter((input) => input.checked).map((input) => input.value));
    this.state.min = this.minInput ? parseDollars(this.minInput.value) : null;
    this.state.max = this.maxInput ? parseDollars(this.maxInput.value) : null;
    this.state.inStock = Boolean(this.stockInput?.checked);
  }

  writeInputs() {
    this.subInputs.forEach((input) => { input.checked = this.state.subs.has(input.value); });
    if (this.minInput) this.minInput.value = this.state.min ?? '';
    if (this.maxInput) this.maxInput.value = this.state.max ?? '';
    if (this.stockInput) this.stockInput.checked = this.state.inStock;
    if (this.sortSelect) this.sortSelect.value = this.state.sort;
  }

  /** The price bounds in cents, swapped if the shopper typed them the wrong way round. */
  get priceBounds() {
    let { min, max } = this.state;
    if (min !== null && max !== null && min > max) [min, max] = [max, min];
    return { low: min === null ? null : min * 100, high: max === null ? null : max * 100 };
  }

  /* Matching --------------------------------------------------------------- */

  matches(item, { skip = null } = {}) {
    const { subs, inStock } = this.state;
    if (skip !== 'subcategory' && subs.size > 0) {
      let hit = false;
      subs.forEach((handle) => { if (item.collections.has(handle)) hit = true; });
      if (!hit) return false;
    }
    const { low, high } = this.priceBounds;
    if (low !== null && item.price < low) return false;
    if (high !== null && item.price > high) return false;
    if (skip !== 'availability' && inStock && !item.available) return false;
    return true;
  }

  sorted(items) {
    const list = items.slice();
    switch (this.state.sort) {
      case 'price-ascending':
        return list.sort((a, b) => a.price - b.price || a.index - b.index);
      case 'price-descending':
        return list.sort((a, b) => b.price - a.price || a.index - b.index);
      case 'created-descending':
        return list.sort((a, b) => b.created.localeCompare(a.created) || a.index - b.index);
      default:
        return list.sort((a, b) => a.index - b.index);
    }
  }

  /* Rendering -------------------------------------------------------------- */

  plural(prefix, count) {
    const key = count === 0 ? 'Zero' : count === 1 ? 'One' : 'Other';
    return (this.dataset[`${prefix}${key}`] || '').replace('[count]', String(count));
  }

  apply({ announce = true, updateUrl = true } = {}) {
    const visible = [];
    this.items.forEach((item) => {
      const show = this.matches(item);
      item.li.hidden = !show;
      if (show) visible.push(item);
    });

    // Reorder only when the order actually changes, so focus and scroll stay put.
    const order = this.sorted(this.items);
    const current = Array.from(this.grid.children);
    if (order.some((item, i) => current[i] !== item.li)) {
      const fragment = document.createDocumentFragment();
      order.forEach((item) => fragment.append(item.li));
      this.grid.append(fragment);
    }

    const count = visible.length;
    const countText = this.plural('count', count);
    if (this.countEl) this.countEl.textContent = countText;
    if (this.showButton) this.showButton.textContent = this.plural('show', count);
    if (announce && this.statusEl) this.statusEl.textContent = countText;

    this.grid.hidden = count === 0;
    if (this.emptyState) this.emptyState.hidden = count !== 0;

    this.updateOptionCounts();
    this.renderChips();
    if (updateUrl) this.writeUrl();
  }

  setOptionCount(input, count) {
    const label = input.closest('label');
    const visibleCount = label?.querySelector('[data-facet-option-count]');
    const srCount = label?.querySelector('[data-facet-option-count-label]');
    if (visibleCount) visibleCount.textContent = String(count);
    if (srCount) srCount.textContent = `, ${this.plural('option', count)}`;
    // An option that would show nothing is disabled, unless it is already on.
    input.disabled = count === 0 && !input.checked;
    label?.classList.toggle('is-disabled', input.disabled);
  }

  updateOptionCounts() {
    this.subInputs.forEach((input) => {
      const count = this.items.filter((item) => item.collections.has(input.value)
        && this.matches(item, { skip: 'subcategory' })).length;
      this.setOptionCount(input, count);
    });
    if (this.stockInput) {
      const count = this.items.filter((item) => item.available && this.matches(item, { skip: 'availability' })).length;
      this.setOptionCount(this.stockInput, count);
    }
  }

  activeFilters() {
    const filters = [];
    this.subInputs.forEach((input) => {
      if (this.state.subs.has(input.value)) {
        filters.push({ type: 'subcategory', value: input.value, label: input.dataset.label || input.value });
      }
    });
    const { min, max } = this.state;
    if (min !== null || max !== null) {
      const { low, high } = this.priceBounds;
      let label;
      if (low !== null && high !== null) {
        label = (this.dataset.chipRange || '')
          .replace('[min]', wholeDollars.format(low / 100))
          .replace('[max]', wholeDollars.format(high / 100));
      } else if (low !== null) {
        label = (this.dataset.chipMin || '').replace('[min]', wholeDollars.format(low / 100));
      } else {
        label = (this.dataset.chipMax || '').replace('[max]', wholeDollars.format(high / 100));
      }
      filters.push({ type: 'price', value: '', label });
    }
    if (this.state.inStock && this.stockInput) {
      filters.push({ type: 'availability', value: '1', label: this.stockInput.dataset.label || '' });
    }
    return filters;
  }

  renderChips() {
    const filters = this.activeFilters();
    if (this.badge) {
      this.badge.hidden = filters.length === 0;
      this.badge.textContent = filters.length ? String(filters.length) : '';
    }
    if (this.badgeLabel) {
      this.badgeLabel.textContent = filters.length
        ? `, ${(this.dataset.activeCount || '').replace('[count]', String(filters.length))}`
        : '';
    }
    if (!this.chips || !this.chipList) return;
    this.chips.hidden = filters.length === 0;
    const removeTemplate = this.dataset.removeLabel || '[label]';
    const chips = filters.map((filter) => {
      const li = document.createElement('li');
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'facet-chip';
      button.dataset.facetChip = filter.type;
      button.dataset.value = filter.value;
      button.setAttribute('aria-label', removeTemplate.replace('[label]', filter.label));
      const text = document.createElement('span');
      text.textContent = filter.label;
      button.append(text, this.closeIcon());
      li.append(button);
      return li;
    });
    this.chipList.replaceChildren(...chips);
  }

  closeIcon() {
    const ns = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(ns, 'svg');
    svg.setAttribute('class', 'icon icon-close facet-chip__icon');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '1.5');
    svg.setAttribute('stroke-linecap', 'round');
    const path = document.createElementNS(ns, 'path');
    path.setAttribute('d', 'M6 6l12 12M18 6L6 18');
    svg.append(path);
    return svg;
  }

  writeUrl() {
    try {
      const params = new URLSearchParams(window.location.search);
      ['filter.p.subcategory', 'filter.v.price.gte', 'filter.v.price.lte', 'filter.v.availability', 'sort_by']
        .forEach((key) => params.delete(key));
      this.state.subs.forEach((handle) => params.append('filter.p.subcategory', handle));
      if (this.state.min !== null) params.set('filter.v.price.gte', String(this.state.min));
      if (this.state.max !== null) params.set('filter.v.price.lte', String(this.state.max));
      if (this.state.inStock) params.set('filter.v.availability', '1');
      if (this.state.sort !== 'manual') params.set('sort_by', this.state.sort);
      const query = params.toString();
      const url = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
      window.history.replaceState(window.history.state, '', url);
    } catch {
      // History changes can be blocked inside sandboxed frames; filtering still works.
    }
  }

  /* Actions ---------------------------------------------------------------- */

  removeChip(chip) {
    const type = chip.dataset.facetChip;
    if (type === 'subcategory') this.state.subs.delete(chip.dataset.value);
    if (type === 'price') {
      this.state.min = null;
      this.state.max = null;
    }
    if (type === 'availability') this.state.inStock = false;
    // Keep keyboard focus in the chip row: move to the next chip, or the grid heading area.
    const li = chip.closest('li');
    const nextIndex = li ? Array.from(li.parentElement.children).indexOf(li) : 0;
    this.writeInputs();
    this.apply();
    const remaining = this.chipList ? Array.from(this.chipList.querySelectorAll('[data-facet-chip]')) : [];
    const target = remaining[Math.min(nextIndex, remaining.length - 1)];
    if (target) target.focus();
    else this.focusAfterClear();
  }

  clearAll(trigger) {
    this.state.subs.clear();
    this.state.min = null;
    this.state.max = null;
    this.state.inStock = false;
    this.writeInputs();
    this.apply();
    // Buttons that disappear with the chips or the empty state hand focus on.
    if (trigger && trigger.closest('[hidden]')) this.focusAfterClear();
  }

  focusAfterClear() {
    const firstLink = this.grid.querySelector(':scope > li:not([hidden]) .card__link');
    if (firstLink) firstLink.focus();
  }
}

defineElement('facet-drawer', FacetDrawer);
defineElement('facet-filters', FacetFilters);
